<script lang="ts">
  import type { Message } from '../lib/types';
  import {
    autolink,
    parseMarkdown,
    type InlineSegment,
    type MarkdownBlock,
  } from '../lib/text';
  import AttachmentPreview from './AttachmentPreview.svelte';

  interface Props {
    message: Message;
    showMeta?: boolean;
    groupEnd?: boolean;
    avatarUrl?: string;
    senderName?: string;
  }

  let {
    message,
    showMeta = true,
    groupEnd = true,
    avatarUrl,
    senderName,
  }: Props = $props();

  const isCustomer = $derived(message.role === 'customer');
  const isSystem = $derived(message.role === 'system');
  // Only AI responses get markdown rendering — user/agent/system keep plain text.
  const isAi = $derived(message.role === 'ai');
  const blocks = $derived<MarkdownBlock[]>(isAi ? parseMarkdown(message.text) : []);
  const plainSegments = $derived<InlineSegment[]>(
    isAi ? [] : autolink(message.text),
  );

  const defaultSenderLabel = $derived.by(() => {
    switch (message.role) {
      case 'customer':
        return '';
      case 'agent':
        return 'Agente';
      case 'ai':
        return 'IA';
      case 'system':
      default:
        return '';
    }
  });

  const senderLabel = $derived.by(() => {
    if (isCustomer) return '';
    if (message.role === 'ai') {
      return senderName && senderName.trim().length > 0
        ? senderName
        : defaultSenderLabel;
    }
    return defaultSenderLabel;
  });

  const timeLabel = $derived.by(() => {
    try {
      const d = new Date(message.created_at);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  });

  const avatarInitial = $derived.by(() => {
    const src = (senderName && senderName.trim()) || defaultSenderLabel;
    return src.charAt(0).toUpperCase() || '?';
  });

  const showAvatar = $derived(
    showMeta && !isCustomer && message.role === 'ai',
  );
</script>

{#snippet renderInline(seg: InlineSegment)}
  {#if seg.kind === 'link' && seg.href}
    <a href={seg.href} target="_blank" rel="noopener noreferrer nofollow ugc">{seg.value}</a>
  {:else if seg.kind === 'bold'}
    <strong>{#each seg.inlines as s, i (i)}{@render renderInline(s)}{/each}</strong>
  {:else if seg.kind === 'italic'}
    <em>{#each seg.inlines as s, i (i)}{@render renderInline(s)}{/each}</em>
  {:else if seg.kind === 'code'}
    <code>{seg.value}</code>
  {:else}
    {seg.value}
  {/if}
{/snippet}

{#if isSystem}
  <div class="hd-bubble hd-bubble--system" role="group">
    <span class="hd-bubble__system-text">{message.text}</span>
  </div>
{:else}
  <div
    class="hd-bubble-row"
    class:hd-bubble-row--self={isCustomer}
    class:hd-bubble-row--group-start={showMeta}
    class:hd-bubble-row--group-end={groupEnd}
    class:hd-bubble-row--with-avatar={showAvatar}
  >
    {#if showMeta && !isCustomer && (senderLabel || timeLabel)}
      {#if message.role === 'ai'}
        <div class="hd-bubble__header">
          {#if showAvatar}
            <div class="hd-bubble__avatar" aria-hidden="true">
              {#if avatarUrl}
                <img src={avatarUrl} alt="" />
              {:else}
                <span class="hd-bubble__avatar-initial">{avatarInitial}</span>
              {/if}
            </div>
          {/if}
          <div class="hd-bubble__meta hd-bubble__meta--header">
            {#if senderLabel}<span class="hd-bubble__sender">{senderLabel}</span>{/if}
            {#if senderLabel && timeLabel}<span class="hd-bubble__meta-dot">·</span>{/if}
            {#if timeLabel}<span class="hd-bubble__time">{timeLabel}</span>{/if}
          </div>
        </div>
      {:else}
        <div class="hd-bubble__meta">
          {#if senderLabel}<span class="hd-bubble__sender">{senderLabel}</span>{/if}
          {#if senderLabel && timeLabel}<span class="hd-bubble__meta-dot">·</span>{/if}
          {#if timeLabel}<span class="hd-bubble__time">{timeLabel}</span>{/if}
        </div>
      {/if}
    {/if}
    {#if showMeta && isCustomer && timeLabel}
      <div class="hd-bubble__meta hd-bubble__meta--self">
        <span class="hd-bubble__time">{timeLabel}</span>
      </div>
    {/if}
    <div class="hd-bubble-row__content">
      {#if !isCustomer && message.role === 'ai'}
        <div class="hd-bubble__avatar-spacer" aria-hidden="true"></div>
      {/if}
      <div
        class="hd-bubble hd-bubble--{message.role}"
        class:hd-bubble--self={isCustomer}
        role="group"
        aria-label={`${message.role} message`}
      >
        <div class="hd-bubble__body">
          {#if isAi}
            {#each blocks as block, bi (bi)}
              {#if block.kind === 'paragraph'}
                <p class="hd-bubble__paragraph">
                  {#each block.inlines as s, i (i)}{@render renderInline(s)}{/each}
                </p>
              {:else if block.kind === 'list' && block.ordered}
                <ol class="hd-bubble__list">
                  {#each block.items as item, i (i)}
                    <li>{#each item as s, j (j)}{@render renderInline(s)}{/each}</li>
                  {/each}
                </ol>
              {:else if block.kind === 'list'}
                <ul class="hd-bubble__list">
                  {#each block.items as item, i (i)}
                    <li>{#each item as s, j (j)}{@render renderInline(s)}{/each}</li>
                  {/each}
                </ul>
              {:else if block.kind === 'code-block'}
                <pre class="hd-bubble__pre"><code>{block.value}</code></pre>
              {/if}
            {/each}
          {:else}
            {#each plainSegments as seg, i (i)}{@render renderInline(seg)}{/each}
          {/if}
          {#if message.status === 'failed'}
            <span class="hd-bubble__status hd-bubble__status--failed">· no se pudo enviar</span>
          {/if}
        </div>
        {#if message.attachments.length > 0}
          <div class="hd-bubble__attachments">
            {#each message.attachments as att (att.storage_key)}
              <AttachmentPreview attachment={att} />
            {/each}
          </div>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .hd-bubble-row {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    max-width: 100%;
    animation: hd-bubble-in 200ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
  }
  .hd-bubble-row--self {
    align-items: flex-end;
  }
  /* Breathing room between groups; consecutive messages in the same group
     stay tucked together (2px row-gap inside the thread). */
  .hd-bubble-row--group-start {
    margin-top: 14px;
  }
  /* First row in the thread has no leading margin — avoids a gap at the top. */
  .hd-bubble-row--group-start:first-child {
    margin-top: 0;
  }
  .hd-bubble-row__content {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    max-width: 100%;
  }
  /* Self rows fill the thread width so the customer bubble can span it. */
  .hd-bubble-row--self,
  .hd-bubble-row--self .hd-bubble-row__content {
    width: 100%;
  }
  .hd-bubble__meta {
    font-size: 11px;
    font-weight: 500;
    line-height: 1.2;
    color: var(--hd-fg-subtle, #94a3b8);
    padding: 0 4px 4px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    letter-spacing: 0.01em;
  }
  .hd-bubble__header {
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: 100%;
    padding-bottom: 4px;
  }
  .hd-bubble__meta--header {
    padding: 0;
  }
  .hd-bubble__meta--self {
    align-self: flex-end;
  }
  .hd-bubble__meta-dot {
    opacity: 0.5;
  }
  .hd-bubble__sender {
    color: var(--hd-fg-muted, #64748b);
    font-weight: 600;
  }
  .hd-bubble__avatar,
  .hd-bubble__avatar-spacer {
    width: 28px;
    height: 28px;
    flex: 0 0 auto;
  }
  .hd-bubble__avatar {
    border-radius: 9999px;
    background: var(--hd-primary, #2563eb);
    color: #ffffff;
    display: grid;
    place-items: center;
    overflow: hidden;
    box-shadow: 0 1px 2px rgb(var(--hd-primary-rgb, 37 99 235) / 0.25);
  }
  .hd-bubble__avatar-spacer {
    /* Invisible placeholder so grouped messages align with the avatar column
       instead of jumping to the thread edge. */
    visibility: hidden;
  }
  .hd-bubble__avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .hd-bubble__avatar-initial {
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: -0.01em;
  }
  .hd-bubble {
    /* `fit-content` lets the bubble hug short text on a single line while
       still wrapping long paragraphs up to the 82% ceiling. Without it the
       flex parent can force an intermediate width that breaks short
       messages like "hola que tal" across two lines. */
    width: fit-content;
    max-width: 82%;
    min-width: 0;
    padding: 9px 13px;
    /* Soft default corners — group start/end classes override the "tucked"
       corners below so a chain reads as a single continuous column. */
    border-radius: 16px;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.55;
    /* `break-word` keeps short messages intact and only breaks truly long
       tokens (URLs, hashes) instead of aggressively splitting at any char. */
    word-wrap: break-word;
    overflow-wrap: break-word;
    background: var(--hd-surface-muted, #f1f5f9);
    color: var(--hd-fg, #0f172a);
    box-shadow: 0 1px 1px rgb(15 23 42 / 0.03);
    transition: background-color 140ms ease;
  }
  /* Preserve newlines typed by the user / agent (plain-text messages).
     AI markdown messages use block elements so they don't need pre-wrap. */
  .hd-bubble--self .hd-bubble__body,
  .hd-bubble--agent .hd-bubble__body {
    white-space: pre-wrap;
  }
  /* ---- Group-aware radius ("bubble chain") ---------------------------- */
  /* AI / agent (left-aligned): the speaker-side corners live on the left.
     Middle & head-of-chain tuck in the bottom-left to 6px; middle & tail-
     of-chain tuck in the top-left. The non-speaker side keeps its 16px. */
  .hd-bubble-row:not(.hd-bubble-row--self):not(.hd-bubble-row--group-end) .hd-bubble {
    border-bottom-left-radius: 6px;
  }
  .hd-bubble-row:not(.hd-bubble-row--self):not(.hd-bubble-row--group-start) .hd-bubble {
    border-top-left-radius: 6px;
  }
  /* The tail: only the last message in a left-aligned chain gets the sharp
     bottom-left corner that makes the bubble feel anchored to a speaker. */
  .hd-bubble-row:not(.hd-bubble-row--self).hd-bubble-row--group-end .hd-bubble {
    border-bottom-left-radius: 6px;
  }

  /* Customer (right-aligned): mirror — speaker-side corners are on the right. */
  .hd-bubble-row--self:not(.hd-bubble-row--group-end) .hd-bubble {
    border-bottom-right-radius: 6px;
  }
  .hd-bubble-row--self:not(.hd-bubble-row--group-start) .hd-bubble {
    border-top-right-radius: 6px;
  }
  .hd-bubble-row--self.hd-bubble-row--group-end .hd-bubble {
    border-bottom-right-radius: 6px;
  }

  /* ---- Bubble palette ------------------------------------------------- */
  /* User bubble: full thread width with text aligned right — reads clearly
     as "the user" and avoids the flex shrink-wrap loop that was breaking
     short messages letter-by-letter. */
  .hd-bubble--self {
    width: 100%;
    max-width: 100%;
    text-align: right;
    background: rgb(var(--hd-primary-rgb, 37 99 235) / 0.4);
    color: var(--hd-fg, #0f172a);
    box-shadow: 0 1px 1px rgb(15 23 42 / 0.05);
  }
  .hd-bubble--agent {
    background: #f1f5f9;
    color: var(--hd-fg, #0f172a);
  }
  .hd-bubble--ai {
    background: #f1f5f9;
    color: var(--hd-fg, #0f172a);
  }
  .hd-bubble--system {
    align-self: center;
    background: transparent;
    color: var(--hd-fg-subtle, #94a3b8);
    font-size: 11px;
    font-weight: 500;
    max-width: 100%;
    padding: 6px 0;
    margin: 4px 0;
    text-align: center;
    letter-spacing: 0.01em;
  }
  @keyframes hd-bubble-in {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .hd-bubble-row {
      animation: none;
    }
  }
  .hd-bubble__system-text {
    display: inline-block;
  }
  .hd-bubble__body a {
    color: var(--hd-primary, #2563eb);
    text-decoration: underline;
    text-underline-offset: 2px;
    word-break: break-word;
  }
  .hd-bubble--self .hd-bubble__body a {
    color: var(--hd-primary, #2563eb);
  }
  /* Markdown blocks inside AI bubbles. */
  .hd-bubble__paragraph {
    margin: 0;
  }
  .hd-bubble__paragraph + .hd-bubble__paragraph,
  .hd-bubble__body > .hd-bubble__paragraph + .hd-bubble__list,
  .hd-bubble__body > .hd-bubble__list + .hd-bubble__paragraph,
  .hd-bubble__body > * + .hd-bubble__pre,
  .hd-bubble__body > .hd-bubble__pre + * {
    margin-top: 8px;
  }
  .hd-bubble__list {
    margin: 0;
    padding-left: 20px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .hd-bubble__list li {
    padding-left: 2px;
  }
  .hd-bubble__pre {
    margin: 0;
    padding: 8px 10px;
    background: rgb(15 23 42 / 0.06);
    border-radius: 8px;
    font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
    font-size: 12.5px;
    line-height: 1.45;
    overflow-x: auto;
  }
  .hd-bubble__body code {
    font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
    font-size: 12.5px;
    background: rgb(15 23 42 / 0.06);
    padding: 1px 5px;
    border-radius: 4px;
  }
  .hd-bubble__body strong {
    font-weight: 600;
  }
  .hd-bubble__attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }
  .hd-bubble__status {
    margin-left: 6px;
    font-size: 11px;
    opacity: 0.75;
  }
  .hd-bubble__status--failed {
    color: #b91c1c;
  }
</style>
